import { Alert, Chip, Typography } from '@mui/material';
import {
  BooleanInput, Datagrid, DateField, FunctionField, List, NumberField, ReferenceInput,
  SearchInput, SelectInput, TextField, useListContext, useRecordContext, type RaRecord
} from 'react-admin';
import { ActiveFilters } from './activeFilters';

const EUR = new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' });

/**
 * Users, and the group that decides what they pay.
 *
 * The group column is the point of this screen: moving someone between groups
 * changes every price they see, so it is shown as a chip rather than buried
 * among the account flags.
 *
 * The legacy profile columns on this 33-column table - facebook_id, gender,
 * maiden_name, about, website - are deliberately absent. So are password and
 * salt, which the API never sends.
 */
const filters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="E-mail alebo meno" />,
  <ReferenceInput key="group" source="user_group_id" reference="groups" label="Skupina">
    <SelectInput optionText="name" label="Skupina" />
  </ReferenceInput>,
  <SelectInput
    key="enabled"
    source="enabled"
    label="Účet"
    choices={[
      { id: 'true', name: 'Aktívny' },
      { id: 'false', name: 'Neaktívny' }
    ]}
  />,
  <BooleanInput
    key="withoutOrders"
    source="withoutOrders"
    label="Vrátane tých bez objednávky"
  />
];

function GroupField() {
  const record = useRecordContext<RaRecord & { groupName: string | null }>();
  if (record == null) return null;

  return record.groupName == null
    ? <Chip size="small" label="Bez skupiny" variant="outlined" />
    : <Chip size="small" color="primary" label={record.groupName} />;
}

function AccountStateField() {
  const record = useRecordContext<RaRecord & { enabled: boolean; locked: boolean; verified: boolean }>();
  if (record == null) return null;

  // Only ever one chip: the worst thing true about the account. Three
  // independent flags side by side make a list harder to scan, not easier.
  if (record.locked) return <Chip size="small" color="error" label="Zablokovaný" />;
  if (!record.enabled) return <Chip size="small" color="default" label="Neaktívny" />;
  if (!record.verified) return <Chip size="small" color="warning" label="Neoverený" variant="outlined" />;
  return <Chip size="small" color="success" label="Aktívny" variant="outlined" />;
}

/**
 * Says who is being listed.
 *
 * Two thirds of the rows in this table have never ordered anything, so the
 * default view leaves them out - and a list that quietly omits most of its
 * rows has to say so, or the count looks wrong.
 */
function AudienceNotice() {
  const { filterValues, total } = useListContext();
  if (filterValues['withoutOrders'] === true) return null;

  return (
    <Alert severity="info" variant="outlined" sx={{ my: 1 }}>
      Zobrazujú sa zákazníci, ktorí už objednali ({total ?? 0}), zoradení od najaktívnejších.
      Účty bez objednávky zobrazíte filtrom „Vrátane tých bez objednávky“.
    </Alert>
  );
}

export function UserList() {
  return (
    <List
      filters={filters}
      // Most active first: the shop's real customers, rather than whoever
      // registered most recently.
      sort={{ field: 'orderCount', order: 'DESC' }}
      perPage={25}
      title="Používatelia"
    >
      <AudienceNotice />
      <ActiveFilters
        ignore={['withoutOrders']}
        labels={{
          q: { label: 'Hľadanie' },
          user_group_id: { label: 'Skupina' },
          enabled: { label: 'Účet', format: value => (value === 'true' ? 'aktívny' : 'neaktívny') }
        }}
      />
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="email" />
        <TextField source="fullName" label="Meno" emptyText="—" />
        <GroupField />
        <NumberField source="orderCount" label="Objednávok" sortable />
        <FunctionField
          label="Nakúpil za"
          sortBy="spent"
          textAlign="right"
          render={(record: RaRecord & { spent: number | null }) =>
            record.spent == null
              ? <Typography variant="caption" color="text.secondary">—</Typography>
              : EUR.format(record.spent)}
        />
        <DateField source="lastOrderAt" label="Posledná objednávka" sortBy="lastOrderAt" emptyText="—" />
        <NumberField source="points" label="Body" />
        <AccountStateField />
      </Datagrid>
    </List>
  );
}
